'use client';

import { motion } from "framer-motion";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Brain, Eye, MessageSquare, Sparkles, Database, Globe, Smartphone, Code, Server } from "lucide-react";
import { useParams } from "next/navigation";
import { localizeSpelling } from "@/lib/spelling-localization";

const servicesData = [
  {
    title: "AI as a Service",
    description: "Custom AI solutions tailored to your business needs with flexible deployment options.",
    icon: Brain,
    features: ["Custom Model Development", "API Integration", "Scalable Infrastructure"],
    gradient: "from-violet-600 via-indigo-500 to-blue-500"
  },
  {
    title: "Fullstack Web Development",
    description: "End-to-end web solutions powered by AI for enhanced functionality and user experience.",
    icon: Globe,
    features: ["AI-Enhanced User Interfaces", "Intelligent Backend Systems", "Real-time Analytics Integration"],
    gradient: "from-cyan-500 via-blue-600 to-purple-600"
  },
  {
    title: "Mobile App Development",
    description: "Native and cross-platform mobile applications with embedded AI capabilities.",
    icon: Smartphone,
    features: ["On-device AI Processing", "Smart User Experiences", "Intelligent Push Notifications"],
    gradient: "from-fuchsia-500 via-purple-600 to-indigo-500"
  },
  {
    title: "Computer Vision",
    description: "Advanced image and video processing solutions for automated visual analysis.",
    icon: Eye,
    features: ["Object Detection", "Image Classification", "Video Analytics"],
    gradient: "from-blue-600 via-sky-500 to-cyan-400"
  },
  {
    title: "Natural Language Processing",
    description: "Text analysis and processing solutions for understanding human language.",
    icon: MessageSquare,
    features: ["Text Classification", "Sentiment Analysis", "Language Understanding"],
    gradient: "from-emerald-500 via-teal-500 to-cyan-500"
  },
  {
    title: "Generative AI",
    description: "Create content and solve complex problems with generative AI models.",
    icon: Sparkles,
    features: ["Text Generation", "Image Generation", "Code Generation"],
    gradient: "from-rose-500 via-pink-600 to-purple-600"
  },
  {
    title: "Data Science Solutions",
    description: "Transform your data into actionable insights with advanced analytics.",
    icon: Database,
    features: ["Predictive Analytics", "Data Visualization", "Business Intelligence"],
    gradient: "from-amber-500 via-orange-600 to-rose-600"
  },
  {
    title: "Frontend Development",
    description: "Beautiful, responsive user interfaces enhanced with AI-powered features.",
    icon: Code,
    features: ["AI-powered UI Components", "Personalized User Experiences", "Smart Form Processing"],
    gradient: "from-green-500 via-emerald-500 to-teal-500"
  },
  {
    title: "Backend Development",
    description: "Robust server-side solutions with integrated AI capabilities.",
    icon: Server, 
    features: ["AI Microservices", "Intelligent APIs", "ML Pipeline Integration"],
    gradient: "from-violet-500 via-purple-500 to-fuchsia-500"
  }
];

export default function Services() {
  const params = useParams();
  const currentLocale = params?.locale as string || 'en';
  
  // Apply localization to all services data
  const services = servicesData.map(service => ({
    ...service,
    title: localizeSpelling(service.title, currentLocale),
    description: localizeSpelling(service.description, currentLocale),
    features: service.features.map(feature => 
      localizeSpelling(feature, currentLocale)
    )
  }));

  return (
    <div className="container mx-auto max-w-6xl px-4 py-16 mt-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-4 mb-12"
      >
        <h1 className="text-4xl font-bold tracking-tight">
          {localizeSpelling("Our Services", currentLocale)}
        </h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          {localizeSpelling("Comprehensive AI solutions to transform your business with cutting-edge technology", currentLocale)}
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service, index) => (
          <motion.div
            key={service.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ 
              y: -5,
              transition: { duration: 0.5 }
            }}
          >
            <div className={`rounded-xl overflow-hidden h-full relative group`}>
              {/* Animated gradient background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-90 group-hover:opacity-100 transition-opacity duration-700`}></div>
              
              {/* Shimmer effect - more subtle with increased blur */}
              <div className="absolute inset-0 backdrop-blur-xl bg-[length:400%_400%] animate-shimmer opacity-10"
                   style={{
                     backgroundImage: 'linear-gradient(145deg, transparent 35%, rgba(255, 255, 255, 0.2) 50%, transparent 65%)',
                     backgroundSize: '400% 400%',
                     filter: 'blur(8px)',
                     animation: 'shimmer 12s infinite linear'
                   }}>
              </div>
              
              {/* Card content */}
              <Card className="h-full border-0 bg-transparent backdrop-blur-sm relative z-10">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg">
                      <service.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-white text-xl">{service.title}</CardTitle>
                      <CardDescription className="text-white/80">{service.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-white/90">
                        <div className="w-1.5 h-1.5 rounded-full bg-white/80" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// Add global CSS for shimmer animation